import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Download, Loader2 } from "lucide-react";
import { api, errorText } from "@/lib/api";

export const nok = (v) => (v === null || v === undefined || v === "" ? "–" : new Intl.NumberFormat("nb-NO", { style: "currency", currency: "NOK", maximumFractionDigits: 0 }).format(v));
export const today = () => new Date().toISOString().slice(0, 10);
export const uid = () => Math.random().toString(36).slice(2, 14);

export const VEHICLE_STATUS = [["innkjopt", "Innkjøpt"], ["transport", "Under transport"], ["klargjoring", "Under klargjøring"], ["til_salgs", "Til salgs"], ["reservert", "Reservert"], ["solgt", "Solgt"]];
export const JOB_STATUS = [["ny", "Ny"], ["pagar", "Pågår"], ["venter_deler", "Venter på deler"], ["fullfort", "Fullført"], ["levert", "Levert"]];
export const CATEGORIES = [["diagnose", "Diagnose"], ["chiptuning", "Chiptuning"], ["ecu_tcu", "ECU/TCU"], ["nokkel", "Nøkkelprogrammering"], ["elektronikk", "Elektronikkreparasjon"], ["annet", "Annet"]];
export const PAYMENT = [["ubetalt", "Ubetalt"], ["delvis", "Delvis betalt"], ["betalt", "Betalt"]];
export const EXPENSE_CATS = [["innkjop", "Innkjøp"], ["transport", "Transport"], ["deler", "Deler"], ["verksted", "Verksted/reparasjon"], ["registrering", "Registrering/avgifter"], ["annet", "Annet"]];
export const label = (list, v) => list.find(([k]) => k === v)?.[1] ?? v;

const base = "w-full border border-white/15 bg-[#08090b] px-3 py-2.5 text-sm text-white outline-none focus:border-[var(--red)]";
export const Field = ({ label: l, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">{l}</span>
    {children}
  </label>
);
export const Input = (p) => <input {...p} className={`${base} ${p.className ?? ""}`} />;
export const Area = (p) => <textarea rows={3} {...p} className={`${base} ${p.className ?? ""}`} />;
export const Select = ({ options, ...p }) => (
  <select {...p} className={`${base} ${p.className ?? ""}`}>{options.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
);
export const Btn = ({ variant = "ghost", className = "", ...p }) => (
  <button type="button" {...p} className={`flex items-center justify-center gap-2 px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-50 ${variant === "red" ? "bg-[var(--red)] text-white hover:brightness-110" : "border border-white/15 text-white hover:border-white/40"} ${className}`} />
);
export const Card = ({ className = "", ...p }) => <div {...p} className={`border border-white/10 bg-[var(--surface)] p-5 ${className}`} />;
export const Badge = ({ children, tone = "zinc" }) => {
  const c = { red: "border-[var(--red)] text-[var(--red)]", green: "border-emerald-500 text-emerald-400", amber: "border-amber-500 text-amber-400", sky: "border-sky-500 text-sky-400", zinc: "border-white/20 text-zinc-300" }[tone];
  return <span className={`inline-block border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${c}`}>{children}</span>;
};
export const PageHead = ({ title, sub, children }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
    <div>
      <h1 className="font-display text-4xl font-black uppercase leading-none md:text-5xl">{title}</h1>
      {sub && <p className="mt-2 font-mono text-xs uppercase tracking-widest text-zinc-400">{sub}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Sheet = ({ title, onClose, children, testid, solid = false }) => (
  <div className={`fixed inset-0 z-[80] overflow-y-auto ${solid ? "bg-[#08090b]" : "bg-[#08090b]/95 backdrop-blur"}`} data-testid={testid} data-lenis-prevent>
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <h2 className="font-display text-3xl font-black uppercase leading-none md:text-4xl">{title}</h2>
        <button onClick={onClose} aria-label="Lukk" data-testid={`${testid}-close`} className="border border-white/15 p-2 hover:border-white/40"><X size={18} /></button>
      </div>
      {children}
    </div>
  </div>
);

export const useData = (url, params) => {
  const [data, setData] = useState(null);
  const key = JSON.stringify(params ?? {});
  const load = useCallback(async () => {
    try {
      const res = await api.get(url, { params: JSON.parse(key) });
      setData(res.data);
    } catch (e) {
      toast.error(errorText(e));
      setData((d) => d ?? []);
    }
  }, [url, key]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
};

export const download = async (url, filename) => {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  } catch (e) {
    toast.error(errorText(e));
  }
};

export const ExportBtn = ({ kind, testid }) => (
  <Btn onClick={() => download(`/admin/export/${kind}.csv`, `rbtun_${kind}_${today()}.csv`)} data-testid={testid}><Download size={14} /> Eksporter CSV</Btn>
);

export const Spinner = () => <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-zinc-400" /></div>;

/* Editable list of rows (expenses, parts, history, vehicles) */
export const RowsEditor = ({ rows, setRows, blank, render, addLabel, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    {rows.map((r, i) => (
      <div key={r.id ?? i} className="grid gap-2 border border-white/10 p-3 md:grid-cols-12 md:items-end">
        {render(r, (patch) => setRows(rows.map((x, j) => (j === i ? { ...x, ...patch } : x))), i)}
        <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Fjern" data-testid={`${testid}-remove-${i}`}
          className="border border-white/15 px-3 py-2.5 text-xs text-zinc-400 hover:border-red-500 hover:text-red-400 md:col-span-1">Fjern</button>
      </div>
    ))}
    <Btn onClick={() => setRows([...rows, { id: uid(), ...blank() }])} data-testid={`${testid}-add`}>+ {addLabel}</Btn>
  </div>
);

export const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
